import React, { useState, useEffect } from 'react';
import { CheckedIconColored, CopyWithArrowIcon } from 'assests/GmsIcons';
import MenuButton from './MenuButton';

interface TopItemsSelectorProps {
    value: TopItemsOption;
    customValue: number;
    onChange: (option: TopItemsOption, customValue?: number) => void;
    disabled?: boolean;
    isOpen: boolean;
    onToggle: () => void;
}

const noSpinnerStyles = `
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }
`;

const TopItemsSelector = ({ value, customValue, onChange, isOpen, onToggle, disabled = false }: TopItemsSelectorProps) => {
    const [tempCustomValue, setTempCustomValue] = useState(customValue.toString());

    useEffect(() => {
        if (isOpen) {
            setTempCustomValue(customValue.toString());
        }
    }, [isOpen, customValue]);

    useEffect(() => {
        if (!isOpen && value === 'custom' && tempCustomValue !== customValue.toString()) {
            const numValue = parseInt(tempCustomValue, 10);
            if (!isNaN(numValue) && numValue > 0) {
                onChange('custom', numValue);
            }
        }
    }, [isOpen, value, tempCustomValue, customValue, onChange]);

    const handleOptionSelect = (option: TopItemsOption) => {
        onChange(option);
        if (option !== 'custom') onToggle();
    };

    const handleCustomValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTempCustomValue(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const numValue = parseInt(tempCustomValue, 10);
            if (!isNaN(numValue) && numValue > 0) {
                onChange('custom', numValue);
                onToggle();
            }
        }
    };

    const displayText = value === 'custom'
        ? `Show Top ${customValue}`
        : `Show Top ${value}`;

    const predefinedOptions: Array<Exclude<TopItemsOption, 'custom'>> = [10, 25, 50];

    return (
        <div className="tw-relative">
            <style>{noSpinnerStyles}</style>

            <MenuButton
                isOpen={isOpen}
                onToggle={onToggle}
                icon={<CopyWithArrowIcon />}
                label={displayText}
                disabled={disabled}
            />

            {isOpen && (
                <div className="tw-w-48 tw-absolute tw-z-10 tw-mt-1 tw-bg-white tw-rounded-md tw-border tw-border-zinc-200 tw-min-w-[160px]">
                    <div>
                        {predefinedOptions.map((option) => (
                            <button
                                key={option}
                                className={`tw-flex tw-justify-between tw-items-center tw-w-full tw-p-2 tw-text-left hover:tw-bg-gray-100 tw-border-b-[0.25px] tw-border-b-zinc-200 ${value === option ? 'tw-bg-gray-100' : ''
                                    }`}
                                onClick={() => handleOptionSelect(option)}
                            >
                                <span className={`${value === option ? 'tw-text-blue-950 tw-font-semibold tw-leading-tight' : ''}`}>
                                    Show Top {option}
                                </span>
                                {value === option && <CheckedIconColored />}
                            </button>
                        ))}

                        <div
                            className={`tw-flex tw-justify-between tw-items-center tw-w-full tw-p-2 tw-text-left hover:tw-bg-gray-100 tw-border-b-[0.25px] tw-border-b-zinc-200 ${value === 'custom' ? 'tw-bg-gray-100' : 'tw-rounded-b-md'
                                }`}
                            onClick={() => handleOptionSelect('custom')}
                        >
                            <span className={`${value === 'custom' ? 'tw-text-blue-950 tw-font-semibold tw-leading-tight' : ''}`}>
                                Custom
                            </span>
                            {value === 'custom' && <CheckedIconColored />}
                        </div>

                        {value === 'custom' && (
                            <div className="tw-mt-2 tw-px-2 tw-pb-2">
                                <div className="tw-justify-start tw-text-slate-500 tw-text-xs tw-font-semibold">Rows per category</div>
                                <div className="tw-mt-1 tw-mr-8">
                                    <input
                                        type="number"
                                        className="tw-w-full tw-py-2 tw-px-[5px] tw-outline tw-outline-1 tw-mb-2 tw-mt-1 tw-outline-black tw-rounded-md"
                                        value={tempCustomValue}
                                        onChange={handleCustomValueChange}
                                        onKeyDown={handleKeyDown}
                                        min="1"
                                        placeholder="Enter"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

TopItemsSelector.displayName = 'TopItemsSelector';

export default TopItemsSelector;